// EXPERIMENT: sign_form_signer_carousel_colors_experiment — remove with experiment cleanup
import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['signFormSignerCarouselColorsExperiment'];

type Params = {
	experiment: Experiment;
	signed: boolean;
};

/**
 * Treatment is deferred to the front-end (treated when the recent-signers carousel enters the viewport),
 * so `treated` is always forced to `false` here — the middle-end can return `treated: true` for a
 * returning session even with `treat: false`.
 *
 * Deferral is the measurement design: only sessions that actually saw the carousel enroll, and that is
 * only known at render time (the carousel needs `MIN_SIGNERS_TO_SHOW` signers and only exists inside the
 * mobile sign modal). Treating here would enroll every petition-details pageview instead.
 */
export function normalizeSignFormSignerCarouselColorsExperiment({ experiment, signed }: Params): Experiment {
	// Out of scope: any `fallback` (not a real assignment), and signers (symmetric across arms). The signed
	// gate scopes enrollment to viewers who are unsigned when page user data loads (guests always read as
	// unsigned: `getPetitionViewerInfo` hard-codes `signed: false` without a `currentUserId`, so a guest who
	// signed in an earlier session stays in scope); it does not stop the
	// carousel recoloring if a refresh later finds the viewer signed. `useTreatSignerCarouselColorsExperiment`
	// snapshots the assignment when the carousel is first seen, so a sign mid-treat cannot drop `$exposure`.
	// The surface (mobile sign modal) and the FCM's country rows define the rest of the audience.
	if (experiment.fallback || signed) return forceControlOffScope(experiment);

	return { ...experiment, treated: false };
}

/**
 * Duplicated in `normalizeSignFormAddressRemovalExperiment.ts` on purpose: keeps each experiment's cleanup
 * self-contained.
 *
 * Resolves an out-of-scope visitor to control **and marks it as a non-assignment**. `fallback` is the
 * treat hook's only signal for "never treat this one": a bare `{ variation: 'control' }` would be
 * identical to genuine control bucketing, so an out-of-scope visitor could be treated and exposed as
 * control.
 */
function forceControlOffScope(experiment: Experiment): Experiment {
	return {
		type: experiment.type,
		experimentName: experiment.experimentName,
		config: experiment.config,
		variation: 'control',
		treated: false,
		fallback: true,
		// Keep a real upstream reason; claim `not-enrolled` only when the exclusion is ours.
		fallbackReason: experiment.fallback ? experiment.fallbackReason : 'not-enrolled',
		// `override` is deliberately not carried through: the resolved-experiment type treats it and
		// `fallback` as mutually exclusive, and the forced control is our scope decision.
	};
}
